import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WorkflowTools from './WorkflowTools';
import WorkflowModal from './WorkflowModal';
import { PolicyDocument } from '../contexts/DocumentContext';
import { useAuth } from '../contexts/AuthContext';
import InsuranceSummaryView from './InsuranceSummaryView';
import CreateProposalModal from './CreateProposalModal';
import DocumentIntelligenceChip from './DocumentIntelligenceChip';
import CombinedDocumentChip from './CombinedDocumentChip';
import DuplicateUploadBanner from './DuplicateUploadBanner';
import SparkleIcon from './icons/SparkleIcon';
import BatchRenameModal from './BatchRenameModal';
import TabBar, { TabBarItem } from './TabBar';
import WorkflowSuggestionCards, { WorkflowSuggestion } from './WorkflowSuggestionCards';
import { useProjectFileDrop } from '../hooks/useProjectFileDrop';
import { useRecentWorkflows } from '../hooks/useRecentWorkflows';
import { useToast } from '../contexts/ToastContext';
import { useQuickStart } from '../contexts/QuickStartContext';
import { fetchWithActivity } from '../utils/fetchWithActivity';
import { POLL_INTERVAL_MS, POLL_TIMEOUT_MS } from '../utils/pollingConstants';
import { formatRelativeDate } from '../utils/formatDate';
import AcordFillModal from './acord/AcordFillModal';
import CertificateModal from './acord/CertificateModal';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import ManualRunModal from './agent/ManualRunModal';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface CombineSuggestion {
  reason: 'same_policy_number' | 'endorsement_of_policy' | 'same_carrier_term';
  confidence: 'high' | 'medium';
  anchorDocumentId: string;
  memberDocumentIds: string[];
  label: string;
}

interface IntelligenceStatusCounts {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  skipped: number;
}

// WorkflowModal only supports pre-filled document/context selection for these
// workflow types today (they use the shared DocumentSourceSelection /
// AdditionalContextStep components) — other suggested types (e.g.
// policy_comparison) launch through a separate wizard outside WorkflowModal.
const LAUNCHABLE_SUGGESTION_TYPES = new Set([
  'yoy_renewal',
  'personal_lines_yoy_renewal',
  'year_over_year',
  'loss_run_analyzer_v4',
  'coverage_analysis',
  'insurance_requirements',
]);

interface Customer {
  id: string;
  name: string;
  type: 'customer' | 'prospect' | 'suspect';
  email?: string;
  phone?: string;
  company?: string;
  lineType?: 'personal' | 'commercial';
  createdAt: Date;
  lastContact?: Date;
  status: 'active' | 'inactive' | 'lead';
  isOwner?: boolean;
  accessType?: 'owned' | 'shared';
  owner?: { id: string; name: string; email: string };
  shareCount?: number;
  permission?: 'viewer' | 'editor';
  ams360CustomerId?: string | null;
}

interface ProjectDetailViewProps {
  selectedCustomer: Customer;
  selectedProject: { id: string; title: string; type?: string } | null;
  customerDetailView: 'workflows' | 'chats' | 'documents' | 'reports' | 'proposals' | 'insurance_summary';
  currentNavigationView: 'customer-selection' | 'project-selection' | 'project-details';

  // Data
  documents: PolicyDocument[];
  customerSelectedDocumentIds: string[];
  customerSelectedReportIds: string[];
  editingReportId: string | null;
  editingReportTitle: string;

  // Pagination
  chatHistoryPage: number;
  reportsPage: number;
  documentsPage: number;

  // Data retrieval functions
  getCustomerDocuments: (customerId: string) => any[];
  getCustomerChats: (customerId: string) => any[];
  getCustomerReports: (customerId: string) => any[];
  getProjectChats: (projectId: string) => any[];
  getProjectReports: (projectId: string) => any[];
  getPaginatedChats: (customerId: string) => any[];
  getPaginatedProjectChats: (projectId: string) => any[];
  getPaginatedReports: (customerId: string) => any[];
  getPaginatedProjectReports: (projectId: string) => any[];
  getPaginatedDocuments: (customerId: string) => any[];
  getPaginatedProjectDocuments: () => any[];
  getTotalChatPages: (customerId: string) => number;
  getTotalProjectChatPages: (projectId: string) => number;
  getTotalReportsPages: (customerId: string) => number;
  getTotalProjectReportsPages: (projectId: string) => number;
  getTotalDocumentsPages: (customerId: string) => number;
  getTotalProjectDocumentsPages: () => number;

  // Event handlers
  onBackToProjects: () => void;
  onConvertToCustomer: (id: string) => void;
  onDetailViewChange: (view: 'workflows' | 'chats' | 'documents' | 'reports' | 'proposals' | 'insurance_summary') => void;
  onShowContactOverlay: () => void;
  onNavigateToWorkflowReport: (chat: any) => void;
  onDeleteChatSession: (id: string, e: React.MouseEvent) => void;
  onDocumentClick: (doc: any) => void;
  onDocumentCheckboxChange: (id: string, checked: boolean) => void;
  onReportCheckboxChange: (id: string, checked: boolean) => void;
  onDeleteDocument: (id: string, name: string, e: React.MouseEvent) => void;
  onReportClick: (report: any) => void;
  onEditReportTitle: (id: string, title: string) => void;
  onSaveReportTitle: (id: string) => void;
  onDeleteReport: (id: string, title: string, e: React.MouseEvent) => void;
  setEditingReportTitle: (title: string) => void;
  onShowUploader: () => void;
  onWorkflowComplete: () => Promise<void>;
  onDocumentsChanged?: () => Promise<void>;

  // Pagination handlers
  onNextChatPage: () => void;
  onPrevChatPage: () => void;
  onNextReportsPage: () => void;
  onPrevReportsPage: () => void;
  onNextDocumentsPage: () => void;
  onPrevDocumentsPage: () => void;

  // Constants
  DOCUMENTS_PER_PAGE: number;

  onShowActivitySummary?: () => void;

  // Insurance Summary deep-link (set by URL param when redirected from a
  // completed-job notification). Consumed once and then cleared by the parent.
  autoOpenInsuranceSummaryId?: string | null;
  onAutoOpenConsumed?: () => void;
}

const REPORTS_PER_PAGE = 6;

const ProjectDetailView: React.FC<ProjectDetailViewProps> = ({
  selectedCustomer,
  selectedProject,
  customerDetailView,
  currentNavigationView,
  documents,
  customerSelectedDocumentIds,
  customerSelectedReportIds,
  editingReportId,
  editingReportTitle,
  chatHistoryPage,
  reportsPage,
  documentsPage,
  getCustomerDocuments,
  getCustomerChats,
  getCustomerReports,
  getProjectChats,
  getProjectReports,
  getPaginatedChats,
  getPaginatedProjectChats,
  getPaginatedReports,
  getPaginatedProjectReports,
  getPaginatedDocuments,
  getPaginatedProjectDocuments,
  getTotalChatPages,
  getTotalProjectChatPages,
  getTotalReportsPages,
  getTotalProjectReportsPages,
  getTotalDocumentsPages,
  getTotalProjectDocumentsPages,
  onBackToProjects,
  onConvertToCustomer,
  onDetailViewChange,
  onShowContactOverlay,
  onNavigateToWorkflowReport,
  onDeleteChatSession,
  onDocumentClick,
  onDocumentCheckboxChange,
  onReportCheckboxChange,
  onDeleteDocument,
  onReportClick,
  onEditReportTitle,
  onSaveReportTitle,
  onDeleteReport,
  setEditingReportTitle,
  onShowUploader,
  onWorkflowComplete,
  onDocumentsChanged,
  onNextChatPage,
  onPrevChatPage,
  onNextReportsPage,
  onPrevReportsPage,
  onNextDocumentsPage,
  onPrevDocumentsPage,
  DOCUMENTS_PER_PAGE,
  onShowActivitySummary,
  autoOpenInsuranceSummaryId,
  onAutoOpenConsumed,
}) => {
  const canEdit = selectedCustomer.isOwner !== false || selectedCustomer.permission === 'editor';
  const { token } = useAuth();
  const { showToast } = useToast();
  const { openQuickStart } = useQuickStart();

  // Documents tab: drag-drop-to-upload + batch upload, active only when
  // viewing a specific project folder (uploads require a projectId).
  const documentsDropTarget = selectedProject && currentNavigationView === 'project-details'
    ? { id: selectedProject.id, customerId: selectedCustomer.id, title: selectedProject.title }
    : { id: '', customerId: '', title: '' };
  const {
    dragProps: documentsDragProps,
    isDragging: documentsIsDragging,
    uploading: documentsUploading,
    uploadError: documentsUploadError,
    duplicateNotice: documentsDuplicateNotice,
    dismissDuplicateNotice: dismissDocumentsDuplicateNotice,
    renameModalProps: documentsRenameModalProps,
  } = useProjectFileDrop({
    project: documentsDropTarget,
    onDocumentsUploaded: async () => {
      if (onDocumentsChanged) await onDocumentsChanged();
    },
  });
  const canDropUpload = !!selectedProject && currentNavigationView === 'project-details';
  const [flashedDocumentId, setFlashedDocumentId] = useState<string | null>(null);
  const flashExistingDocument = (documentId: string) => {
    document.getElementById(`doc-card-${documentId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashedDocumentId(documentId);
    setTimeout(() => setFlashedDocumentId((prev) => (prev === documentId ? null : prev)), 2000);
  };

  // Card vs list view for the folder workspace (workflows, chats, documents,
  // reports, proposals). Persisted so the choice sticks across folders/sessions.
  const [contentViewMode, setContentViewMode] = useState<'card' | 'list'>(() => {
    const saved = localStorage.getItem('riskninja-folder-content-view-mode');
    return saved === 'list' ? 'list' : 'card';
  });
  useEffect(() => {
    localStorage.setItem('riskninja-folder-content-view-mode', contentViewMode);
  }, [contentViewMode]);

  // Automatic document-grouping suggestions ("these look like parts of one
  // policy — combine them?"). Dismissal is persisted server-side (E6) —
  // local state below is just for optimistic hiding before the next fetch.
  const [combineSuggestions, setCombineSuggestions] = useState<CombineSuggestion[]>([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());
  const [applyingSuggestionKey, setApplyingSuggestionKey] = useState<string | null>(null);

  const suggestionKey = (s: CombineSuggestion) => `${s.anchorDocumentId}:${s.memberDocumentIds.join(',')}`;

  const dismissCombineSuggestion = async (suggestion: CombineSuggestion) => {
    setDismissedSuggestions((prev) => new Set(prev).add(suggestionKey(suggestion)));
    try {
      await fetchWithActivity(`${API_BASE_URL}/api/document-combination/suggestions/${selectedProject?.id}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          anchorDocumentId: suggestion.anchorDocumentId,
          memberDocumentIds: suggestion.memberDocumentIds,
        }),
      });
    } catch {
      // Non-critical — worst case the banner reappears on next load.
    }
  };

  const fetchCombineSuggestions = useCallback(async () => {
    if (currentNavigationView !== 'project-details' || !selectedProject?.id) {
      setCombineSuggestions([]);
      return;
    }
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/document-combination/suggestions/${selectedProject.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setCombineSuggestions(await res.json());
      }
    } catch {
      // Non-critical — the documents list still works without suggestions.
    }
  }, [currentNavigationView, selectedProject?.id, token]);

  useEffect(() => {
    fetchCombineSuggestions();
  }, [fetchCombineSuggestions]);

  const applyCombineSuggestion = async (suggestion: CombineSuggestion) => {
    if (!selectedProject?.id) return;
    const key = suggestionKey(suggestion);
    setApplyingSuggestionKey(key);
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/document-combination/apply-suggestion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          projectId: selectedProject.id,
          anchorDocumentId: suggestion.anchorDocumentId,
          memberDocumentIds: suggestion.memberDocumentIds,
        }),
      });
      if (res.ok) {
        showToast({ type: 'success', title: 'Documents Combined', message: `Risk Ninja combined ${suggestion.memberDocumentIds.length + 1} related documents for you.`, duration: 5000 });
        setCombineSuggestions((prev) => prev.filter((s) => suggestionKey(s) !== key));
        if (onDocumentsChanged) await onDocumentsChanged();
      } else if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        showToast({
          type: 'warning',
          title: 'Could Not Combine',
          message: `These documents couldn't be combined automatically${data.failedDocument ? ` (${data.failedDocument})` : ''} — you can still select them together in a workflow.`,
          duration: 7000,
        });
      } else {
        showToast({ type: 'error', title: 'Combine Failed', message: 'Something went wrong combining these documents.', duration: 5000 });
      }
    } catch {
      showToast({ type: 'error', title: 'Combine Failed', message: 'Something went wrong combining these documents.', duration: 5000 });
    } finally {
      setApplyingSuggestionKey(null);
    }
  };

  // Recommended workflow suggestions ("this project looks ready for X").
  const [workflowSuggestions, setWorkflowSuggestions] = useState<WorkflowSuggestion[]>([]);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
  const [runningSuggestion, setRunningSuggestion] = useState<WorkflowSuggestion | null>(null);
  const { recordUsage: recordRecentWorkflowUsage } = useRecentWorkflows();
  const documentNamesById = useMemo(() => new Map(documents.map((d) => [d.id, d.name])), [documents]);

  const fetchWorkflowSuggestions = useCallback(async (): Promise<WorkflowSuggestion[]> => {
    if (currentNavigationView !== 'project-details' || !selectedProject?.id) {
      setWorkflowSuggestions([]);
      setSuggestionsLoaded(true);
      return [];
    }
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/workflows/suggestions/${selectedProject.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data: WorkflowSuggestion[] = await res.json();
        const filtered = data.filter((s) => LAUNCHABLE_SUGGESTION_TYPES.has(s.workflowType));
        setWorkflowSuggestions(filtered);
        setSuggestionsLoaded(true);
        return filtered;
      }
    } catch {
      // Non-critical — the Workflows tab still works without recommendations.
    }
    setSuggestionsLoaded(true);
    return [];
  }, [currentNavigationView, selectedProject?.id, token]);

  useEffect(() => {
    fetchWorkflowSuggestions();
  }, [fetchWorkflowSuggestions]);

  // Highlight a suggestion card briefly when it first appears (vs. the
  // previous fetch) — reset the baseline whenever the project changes so
  // switching projects doesn't highlight every card in the new one.
  const prevSuggestionKeysRef = React.useRef<Set<string> | null>(null);
  const [highlightedSuggestionKeys, setHighlightedSuggestionKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    prevSuggestionKeysRef.current = null;
    prevCombinedDocIdsRef.current = null;
    prevDocIdsRef.current = null;
    setCombinedCreatedCount(0);
  }, [selectedProject?.id]);

  // Tracks newly-appeared combined documents (auto-combine firing while the
  // user watches) so the analysis strip's completion line can mention it.
  const prevCombinedDocIdsRef = React.useRef<Set<string> | null>(null);
  const [combinedCreatedCount, setCombinedCreatedCount] = useState(0);
  useEffect(() => {
    const currentCombinedIds = new Set(documents.filter((d) => d.policyType === 'combined').map((d) => d.id));
    if (prevCombinedDocIdsRef.current) {
      const newCount = Array.from(currentCombinedIds).filter((id) => !prevCombinedDocIdsRef.current!.has(id)).length;
      if (newCount > 0) setCombinedCreatedCount((c) => c + newCount);
    }
    prevCombinedDocIdsRef.current = currentCombinedIds;
  }, [documents]);

  useEffect(() => {
    const currentKeys = new Set(workflowSuggestions.map((s) => s.suggestionKey));
    if (prevSuggestionKeysRef.current) {
      const newKeys = Array.from(currentKeys).filter((k) => !prevSuggestionKeysRef.current!.has(k));
      if (newKeys.length > 0) {
        setHighlightedSuggestionKeys(new Set(newKeys));
        const timer = setTimeout(() => setHighlightedSuggestionKeys(new Set()), 2000);
        prevSuggestionKeysRef.current = currentKeys;
        return () => clearTimeout(timer);
      }
    }
    prevSuggestionKeysRef.current = currentKeys;
  }, [workflowSuggestions]);

  // Project-level extraction status polling — powers the "still analyzing"
  // strip (E2) and keeps recommendations live-updating (E1) while extraction
  // is in flight. Mirrors DocumentIntelligenceChip's poll/mountedRef pattern.
  const [intelStatus, setIntelStatus] = useState<IntelligenceStatusCounts | null>(null);
  const [analysisStripState, setAnalysisStripState] = useState<'hidden' | 'analyzing' | 'success'>('hidden');
  const intelPollTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const intelPollStartedAtRef = React.useRef<number>(Date.now());
  const intelMountedRef = React.useRef(true);
  const wasInFlightRef = React.useRef(false);
  const suggestionCountBeforePollRef = React.useRef(0);
  const successTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Speculative extra poll passes after a restart, covering the gap between
  // the documents list refreshing and the fire-and-forget extraction actually
  // creating its document_intelligence row.
  const gracePollsRef = React.useRef(0);

  useEffect(() => {
    intelMountedRef.current = true;
    return () => {
      intelMountedRef.current = false;
      if (intelPollTimerRef.current) clearTimeout(intelPollTimerRef.current);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const pollIntelligenceStatus = useCallback(async () => {
    if (currentNavigationView !== 'project-details' || !selectedProject?.id) return;
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/intelligence/project/${selectedProject.id}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!intelMountedRef.current || !res.ok) return;
      const status: IntelligenceStatusCounts = await res.json();
      if (!intelMountedRef.current) return;
      setIntelStatus(status);

      const inFlight = status.pending + status.processing > 0;
      const justStarted = inFlight && !wasInFlightRef.current;
      const justFinished = !inFlight && wasInFlightRef.current;
      wasInFlightRef.current = inFlight;

      if (justStarted) {
        suggestionCountBeforePollRef.current = workflowSuggestions.length;
        setCombinedCreatedCount(0);
        setAnalysisStripState('analyzing');
      }

      if (inFlight) {
        setAnalysisStripState('analyzing');
        await fetchWorkflowSuggestions();
      } else if (justFinished) {
        const latest = await fetchWorkflowSuggestions();
        if (intelMountedRef.current && latest.length > suggestionCountBeforePollRef.current) {
          setAnalysisStripState('success');
          if (successTimerRef.current) clearTimeout(successTimerRef.current);
          successTimerRef.current = setTimeout(() => {
            if (intelMountedRef.current) {
              setAnalysisStripState('hidden');
              setCombinedCreatedCount(0);
            }
          }, 10000);
        } else {
          setAnalysisStripState('hidden');
        }
      }

      const graceRemaining = !inFlight && gracePollsRef.current > 0;
      if (graceRemaining) gracePollsRef.current -= 1;
      if (inFlight) gracePollsRef.current = 0; // extraction seen — grace no longer needed
      if ((inFlight || graceRemaining) && Date.now() - intelPollStartedAtRef.current < POLL_TIMEOUT_MS) {
        intelPollTimerRef.current = setTimeout(pollIntelligenceStatus, POLL_INTERVAL_MS);
      }
    } catch {
      // Non-critical — the strip simply won't update until the next mount.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNavigationView, selectedProject?.id, token, fetchWorkflowSuggestions]);

  useEffect(() => {
    if (intelPollTimerRef.current) clearTimeout(intelPollTimerRef.current);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    if (currentNavigationView !== 'project-details' || !selectedProject?.id) {
      setIntelStatus(null);
      setAnalysisStripState('hidden');
      return;
    }
    intelPollStartedAtRef.current = Date.now();
    wasInFlightRef.current = false;
    gracePollsRef.current = 0;
    setAnalysisStripState('hidden');
    pollIntelligenceStatus();
    return () => {
      if (intelPollTimerRef.current) clearTimeout(intelPollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, currentNavigationView]);

  // Restart the extraction-status poll whenever new documents appear (any
  // upload path: drop, Upload button, modal uploaders). Watching the prop
  // beats wiring callbacks through every uploader.
  const prevDocIdsRef = React.useRef<Set<string> | null>(null);
  useEffect(() => {
    const currentIds = new Set(documents.map((d) => d.id));
    const hadPrev = prevDocIdsRef.current !== null;
    const newIds = hadPrev ? Array.from(currentIds).filter((id) => !prevDocIdsRef.current!.has(id)) : [];
    prevDocIdsRef.current = currentIds;
    if (!hadPrev || newIds.length === 0) return;
    if (currentNavigationView !== 'project-details' || !selectedProject?.id) return;
    if (intelPollTimerRef.current) clearTimeout(intelPollTimerRef.current);
    intelPollStartedAtRef.current = Date.now();   // fresh 3-minute window
    gracePollsRef.current = 2;                     // see gracePollsRef comment above
    pollIntelligenceStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  const scrollToRecommendations = () => {
    const scroll = () => document.getElementById('recommendation-band')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (customerDetailView !== 'workflows') {
      onDetailViewChange('workflows');
      setTimeout(scroll, 50);
    } else {
      scroll();
    }
  };

  const renderAnalysisStrip = () => {
    if (analysisStripState === 'hidden' || !intelStatus) return null;
    if (analysisStripState === 'analyzing') {
      const analyzed = Math.max(0, intelStatus.total - (intelStatus.pending + intelStatus.processing));
      return (
        <div className="mb-4 px-3 py-2 bg-blue-600 text-white text-xs rounded-lg flex items-center gap-2">
          <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin flex-shrink-0" aria-hidden="true" />
          <span>Analyzing {analyzed} of {intelStatus.total} document{intelStatus.total === 1 ? '' : 's'} — recommendations will update automatically.</span>
        </div>
      );
    }
    return (
      <div className="mb-4 px-3 py-2 bg-blue-600 text-white text-xs rounded-lg flex items-center justify-between gap-2">
        <span>
          Analysis complete — {workflowSuggestions.length} workflow{workflowSuggestions.length === 1 ? '' : 's'} recommended
          {combinedCreatedCount > 0 && ` · ${combinedCreatedCount} combined document${combinedCreatedCount === 1 ? '' : 's'} created`}
        </span>
        <button onClick={scrollToRecommendations} className="underline font-medium flex items-center gap-1 flex-shrink-0">
          View <span aria-hidden="true">↓</span>
        </button>
      </div>
    );
  };

  const dismissWorkflowSuggestion = async (s: WorkflowSuggestion, reason?: 'wrong_documents' | 'already_done' | 'not_relevant') => {
    if (!selectedProject?.id) return;
    setWorkflowSuggestions((prev) => prev.filter((x) => x.suggestionKey !== s.suggestionKey));
    try {
      await fetchWithActivity(`${API_BASE_URL}/api/workflows/suggestions/${selectedProject.id}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ suggestionKey: s.suggestionKey, reason }),
      });
    } catch {
      // Non-critical — worst case the card reappears on next load.
    }
  };

  // Insights "Start Renewal" deep link (?suggest=renewal) — auto-open the
  // best-matching renewal suggestion once suggestions have loaded; consume
  // the flag so it doesn't re-trigger on tab switches.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const suggest = searchParams.get('suggest');
    const runKey = searchParams.get('run');
    if (!suggest && !runKey) return;
    if (!suggestionsLoaded) return;

    if (suggest === 'renewal') {
      const renewalSuggestion = workflowSuggestions.find((s) =>
        ['yoy_renewal', 'personal_lines_yoy_renewal', 'year_over_year'].includes(s.workflowType)
      );
      if (renewalSuggestion) setRunningSuggestion(renewalSuggestion);
    }

    // ?run=<suggestionKey> — generic "land here and auto-open this exact
    // recommendation" deep link (e.g. from the intake modal's "Run with
    // these documents" right after filing a new project — SMART_INTAKE_PLAN.md).
    if (runKey) {
      const exact = workflowSuggestions.find((s) => s.suggestionKey === runKey);
      if (exact) setRunningSuggestion(exact);
    }

    const next = new URLSearchParams(searchParams);
    next.delete('suggest');
    next.delete('run');
    setSearchParams(next, { replace: true });
  }, [searchParams, suggestionsLoaded, workflowSuggestions, setSearchParams]);

  const isList = contentViewMode === 'list';

  // ACORD forms / certificates / "Run Arie on this folder" (AGENT_MAILBOX_PLAN.md §8.2).
  const [folderAction, setFolderAction] = useState<null | 'acord' | 'certificate' | 'agent'>(null);
  // ACORD + certificates stay hidden until we're licensed (ACORD_ENABLED).
  const features = useFeatureFlags();
  const navigateTo = useNavigate();
  const customerLabel = selectedCustomer!.lineType === 'personal' ? selectedCustomer!.name : (selectedCustomer!.company || selectedCustomer!.name);

  return (
    <div>
      {/* Header with Back Button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBackToProjects}
            className="flex items-center gap-2 px-3 py-2 text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
            </svg>
            Back to List
          </button>
          <div>
            <h2 className="text-xl font-semibold text-secondary dark:text-dark-text flex items-center gap-2">
              <span className="text-accent dark:text-dark-muted font-normal">
                {selectedCustomer!.lineType === 'personal' ? selectedCustomer!.name : (selectedCustomer!.company || selectedCustomer!.name)}
              </span>
              <span className="text-gray-300 dark:text-gray-600">/</span>
              <span>{selectedProject?.title}</span>
              {onShowActivitySummary && selectedProject && (
                <button
                  onClick={onShowActivitySummary}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
                  title="Project activity summary"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="9" strokeWidth={2} />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4M12 8h.01" />
                  </svg>
                </button>
              )}
              <button
                onClick={onShowContactOverlay}
                className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-primary hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
                title="Contact info"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                </svg>
              </button>
            </h2>
            <p className="text-sm text-accent dark:text-dark-muted">
              {selectedCustomer!.type === 'customer' ? 'Customer' :
               selectedCustomer!.type === 'prospect' ? 'Prospect' :
               'Suspect'} • {selectedProject?.type?.replace('_', ' ')} project
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedProject && (
            <>
              {features.acord && (
                <>
              <button
                onClick={() => setFolderAction('acord')}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-secondary dark:text-dark-text rounded-lg hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors"
                title="Fill an ACORD form from what's on file"
              >
                ACORD form
              </button>
              <button
                onClick={() => setFolderAction('certificate')}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-secondary dark:text-dark-text rounded-lg hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors"
                title="Certificate of insurance or Certificate of Effort"
              >
                Certificate
              </button>
                </>
              )}
              <button
                onClick={() => setFolderAction('agent')}
                className="px-3 py-2 text-sm border border-primary/40 text-primary rounded-lg hover:bg-primary/5 transition-colors"
                title="Give Arie instructions to carry out on this folder"
              >
                Run Arie…
              </button>
            </>
          )}
          {(selectedCustomer!.type === 'prospect' || selectedCustomer!.type === 'suspect') && (
          <button
              onClick={() => onConvertToCustomer(selectedCustomer!.id)}
              className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
          >
              Convert to Customer
          </button>
      )}
          </div>
        </div>

      {features.acord && folderAction === 'acord' && selectedProject && (
        <AcordFillModal
          customerId={selectedCustomer!.id}
          projectId={selectedProject.id}
          customerName={customerLabel || ''}
          onClose={() => setFolderAction(null)}
          onFilled={() => { onDocumentsChanged?.(); }}
        />
      )}
      {features.acord && folderAction === 'certificate' && selectedProject && (
        <CertificateModal
          customerId={selectedCustomer!.id}
          projectId={selectedProject.id}
          customerName={customerLabel || ''}
          documents={documents.map((d) => ({ id: d.id, name: d.name }))}
          onClose={() => setFolderAction(null)}
          onCreated={() => { onDocumentsChanged?.(); }}
        />
      )}
      {folderAction === 'agent' && selectedProject && (
        <ManualRunModal
          customerId={selectedCustomer!.id}
          projectId={selectedProject.id}
          customerName={customerLabel || ''}
          projectName={selectedProject.title}
          onClose={() => setFolderAction(null)}
          onStarted={(runId) => { setFolderAction(null); navigateTo(`/agent?run=${runId}`); }}
        />
      )}

      {/* Tab bar */}
      <TabBar
        activeKey={customerDetailView}
        onChange={(key) => onDetailViewChange(key as typeof customerDetailView)}
        items={([
          {
            key: 'workflows',
            label: 'Run Workflow',
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            )
          },
          {
            key: 'documents',
            label: 'Documents',
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
            ),
            badge: currentNavigationView === 'project-details' ? documents.length : getCustomerDocuments(selectedCustomer!.id).length
          },
          {
            key: 'reports',
            label: 'Reports',
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9,12H15V10.5H9V12M9,16H15V14.5H9V16M9,8H15V6.5H9V8M5,4V20A2,2 0 0,0 7,22H17A2,2 0 0,0 19,20V4A2,2 0 0,0 17,2H7A2,2 0 0,0 5,4M7,4H17V20H7V4Z" />
              </svg>
            ),
            badge: currentNavigationView === 'project-details' ? getProjectReports(selectedProject!.id).length : getCustomerReports(selectedCustomer!.id).length
          },
          {
            key: 'chats',
            label: 'Chat History',
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,3C6.5,3 2,6.58 2,11A7.18,7.18 0 0,0 2.64,14.25L1,22L8.75,20.36C9.81,20.75 10.87,21 12,21C17.5,21 22,17.42 22,13C22,8.58 17.5,5 12,5M12,19C11,19 10.03,18.75 9.18,18.5L8.5,18.25L4.5,19.5L5.75,15.5L5.5,14.82C5.25,13.97 5,13 5,12A6,6 0 0,1 12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18Z" />
              </svg>
            ),
            badge: currentNavigationView === 'project-details' ? getProjectChats(selectedProject!.id).length : getCustomerChats(selectedCustomer!.id).length
          },
          {
            key: 'insurance_summary',
            label: 'Insurance Summary',
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z" />
              </svg>
            )
          },
          {
            key: 'proposals',
            label: 'Proposals',
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M9,13V15H15V13H9M9,17V19H12V17H9M9,9V11H15V9H9Z" />
              </svg>
            )
          }
        ] as TabBarItem[])}
        trailing={
          ['chats', 'documents', 'reports'].includes(customerDetailView) ? (
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1" role="group" aria-label="Content view mode">
              <button
                onClick={() => setContentViewMode('card')}
                className={`px-2.5 py-1.5 rounded-md transition-colors ${!isList ? 'bg-white dark:bg-gray-800 shadow-sm text-primary' : 'text-gray-500 dark:text-gray-400'}`}
                title="Card view"
                aria-pressed={!isList}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM13 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1V5zM4 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5zM13 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1v-5z" />
                </svg>
              </button>
              <button
                onClick={() => setContentViewMode('list')}
                className={`px-2.5 py-1.5 rounded-md transition-colors ${isList ? 'bg-white dark:bg-gray-800 shadow-sm text-primary' : 'text-gray-500 dark:text-gray-400'}`}
                title="List view"
                aria-pressed={isList}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          ) : undefined
        }
      />

      {/* Content Area */}
      {customerDetailView === 'workflows' ? (
        <div>
          {renderAnalysisStrip()}
          {/* Recommended Workflows — the hero of this tab when present */}
          {workflowSuggestions.length > 0 && (
            <div id="recommendation-band" className="mb-8">
              <WorkflowSuggestionCards
                suggestions={workflowSuggestions}
                documentNames={documentNamesById}
                isList={isList}
                highlightedSuggestionKeys={highlightedSuggestionKeys}
                onDismiss={dismissWorkflowSuggestion}
                onRun={(s) => {
                  recordRecentWorkflowUsage(s.workflowType);
                  setRunningSuggestion(s);
                }}
              />
            </div>
          )}
          {workflowSuggestions.length === 0 && (intelStatus ? intelStatus.pending + intelStatus.processing > 0 : documents.some((d) => d.status === 'processing' || d.status === 'ocr_processing' || d.status === 'uploading')) && (
            <p className="text-sm text-accent dark:text-dark-muted mb-8">
              Risk Ninja is analyzing this project's documents — recommendations will appear here.
            </p>
          )}

          {/* Teaching empty state — no documents in the project at all, distinct
              from the "analyzing" placeholder above (docs present but extracting). */}
          {currentNavigationView === 'project-details' && documents.length === 0 && (
            <div className="mb-8 p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm text-accent dark:text-dark-muted">
                <p>Upload documents first — Risk Ninja will recommend workflows based on what it finds.</p>
                <p className="mt-1">
                  …or let{' '}
                  <button onClick={() => openQuickStart()} className="text-primary hover:underline font-medium inline-flex items-center gap-1">
                    <SparkleIcon className="w-3.5 h-3.5" />
                    Arie
                  </button>{' '}
                  walk you through it.
                </p>
              </div>
              <button
                onClick={onShowUploader}
                className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors flex-shrink-0"
              >
                Upload
              </button>
            </div>
          )}

          {/* Workflow Tools Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-secondary dark:text-dark-text mb-4">
              All workflows
            </h3>
            <WorkflowTools
              clientType={selectedCustomer!.type}
              clientId={selectedCustomer!.id}
              clientName={
                selectedCustomer!.lineType === 'personal'
                  ? selectedCustomer!.name
                  : (selectedCustomer!.company || selectedCustomer!.name)
              }
              contactName={
                // Show the contact line whenever the display name differs from
                // the raw `name` field (i.e. commercial customer with a
                // separate contact). Treat missing lineType the same as
                // commercial — matches CustomerSelectionView's display logic.
                selectedCustomer!.lineType !== 'personal' && selectedCustomer!.company
                  ? selectedCustomer!.name
                  : undefined
              }
              projectId={selectedProject?.id}
              projectName={selectedProject?.title}
              onWorkflowComplete={onWorkflowComplete}
              viewMode={contentViewMode}
              autoLaunchWorkflowType={searchParams.get('workflow') || undefined}
              onAutoLaunchConsumed={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('workflow');
                setSearchParams(next, { replace: true });
              }}
            />
          </div>
        </div>
      ) : customerDetailView === 'chats' ? (
        <div>
          {/* Chat History Tiles */}
          <div className={isList ? 'flex flex-col gap-2 mb-6' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6'}>
            {(currentNavigationView === 'project-details' ? getPaginatedProjectChats(selectedProject!.id) : getPaginatedChats(selectedCustomer!.id)).map((chat) => (
              <div
                key={chat.id}
                className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow cursor-pointer group ${isList ? 'px-4 py-2.5' : 'p-4'}`}
                onClick={() => {
                  if (!chat.reportId) {
                    showToast({ type: 'info', title: 'Chat only', message: 'This conversation has no report attached yet — resuming isn\'t available.', duration: 4000 });
                    return;
                  }
                  onNavigateToWorkflowReport(chat);
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-blue-600 dark:text-blue-400">
                        <path d="M12,3C6.5,3 2,6.58 2,11A7.18,7.18 0 0,0 2.64,14.25L1,22L8.75,20.36C9.81,20.75 10.87,21 12,21C17.5,21 22,17.42 22,13C22,8.58 17.5,5 12,5" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-secondary dark:text-dark-text text-sm leading-tight break-words flex items-center gap-1.5">
                        <span className="truncate">{chat.title || 'Untitled Chat'}</span>
                        {chat.reportId && (
                          <span title="Report attached" className="inline-flex flex-shrink-0 text-green-600 dark:text-green-400">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                            </svg>
                          </span>
                        )}
                      </h4>
                      {chat.lastMessage && (
                        <p className="text-xs text-accent dark:text-dark-muted truncate mt-0.5">{chat.lastMessage}</p>
                      )}
                      {!chat.isOwner && chat.createdByName && (
                        <p className="text-xs text-accent dark:text-dark-muted mt-0.5">by {chat.createdByName}</p>
                      )}
                    </div>
                  </div>
              {canEdit && (
                <button
                  onClick={(e) => onDeleteChatSession(chat.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 transition-opacity flex-shrink-0 ml-2"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                  </svg>
                </button>
              )}
                </div>
                <div className={isList ? 'flex items-center gap-4' : 'space-y-2'}>
                  <div className="flex items-center gap-2 text-sm text-accent dark:text-dark-muted">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" />
                    </svg>
                    {formatRelativeDate(chat.lastActivity ?? chat.updatedAt ?? chat.createdAt)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-accent dark:text-dark-muted">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,21.9 9.75,22 9.5,22V22H9Z" />
                    </svg>
                    {chat.messageCount ?? 0} messages
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {(currentNavigationView === 'project-details' ? getTotalProjectChatPages(selectedProject!.id) : getTotalChatPages(selectedCustomer!.id)) > 1 && (
            <div className="flex items-center justify-center gap-4">
            <button
                onClick={onPrevChatPage}
                disabled={chatHistoryPage === 0}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
                </svg>
                Previous
              </button>
              <span className="text-sm text-accent dark:text-dark-muted">
                Page {chatHistoryPage + 1} of {(currentNavigationView === 'project-details' ? getTotalProjectChatPages(selectedProject!.id) : getTotalChatPages(selectedCustomer!.id))}
              </span>
              <button
                onClick={onNextChatPage}
                disabled={chatHistoryPage >= (currentNavigationView === 'project-details' ? getTotalProjectChatPages(selectedProject!.id) : getTotalChatPages(selectedCustomer!.id)) - 1}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                Next
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z" />
                </svg>
            </button>
          </div>
          )}

          {/* Empty State */}
          {(currentNavigationView === 'project-details' ? getProjectChats(selectedProject!.id).length : getCustomerChats(selectedCustomer!.id).length) === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400">
                  <path d="M12,3C6.5,3 2,6.58 2,11A7.18,7.18 0 0,0 2.64,14.25L1,22L8.75,20.36C9.81,20.75 10.87,21 12,21C17.5,21 22,17.42 22,13C22,8.58 17.5,5 12,5" />
                </svg>
        </div>
              <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-2">
                No chat sessions yet
              </h3>
              <p className="text-accent dark:text-dark-muted">
                Start a conversation with this customer using the chat interface below.
        </p>
      </div>
    )}
        </div>
      ) : customerDetailView === 'documents' ? (
        <div className="relative" {...(canDropUpload ? documentsDragProps : {})}>
          {renderAnalysisStrip()}
          {/* Drop overlay */}
          {canDropUpload && documentsIsDragging && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-blue-50/95 dark:bg-blue-900/40 rounded-lg pointer-events-none border-2 border-dashed border-primary">
              <svg className="w-8 h-8 text-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.9 5 5 0 119.9 1H21a3 3 0 010 6h-1m-5 1l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm font-medium text-primary">Drop to upload to {selectedProject?.title}</span>
            </div>
          )}

          {/* Uploading / error / duplicate notices */}
          {documentsUploading && (
            <div className="mb-4 px-3 py-2 bg-blue-600 text-white text-xs rounded-lg flex items-center justify-between">
              <span>Uploading {documentsUploading.done}/{documentsUploading.total}…</span>
              <div className="w-24 h-1.5 bg-blue-900/40 rounded-full overflow-hidden">
                <div className="h-full bg-white" style={{ width: `${(documentsUploading.done / documentsUploading.total) * 100}%` }} />
              </div>
            </div>
          )}
          {documentsUploadError && (
            <div className="mb-4 px-3 py-2 bg-red-600 text-white text-xs rounded-lg">{documentsUploadError}</div>
          )}
          {!documentsUploadError && documentsDuplicateNotice && (
            <DuplicateUploadBanner
              notice={documentsDuplicateNotice}
              token={token}
              onDismiss={dismissDocumentsDuplicateNotice}
              onViewExisting={() => flashExistingDocument(documentsDuplicateNotice.duplicateOfDocumentId)}
              onRemoved={async () => {
                dismissDocumentsDuplicateNotice();
                if (onDocumentsChanged) await onDocumentsChanged();
              }}
            />
          )}

          {/* Header: document count + persistent Upload button (visible even when documents already exist) */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-secondary dark:text-dark-text">
              Documents
              {(currentNavigationView === 'project-details' ? documents.length : getCustomerDocuments(selectedCustomer!.id).length) > 0 && (
                <span className="ml-2 text-sm font-normal text-accent dark:text-dark-muted">
                  ({currentNavigationView === 'project-details' ? documents.length : getCustomerDocuments(selectedCustomer!.id).length})
                </span>
              )}
            </h3>
            <button
              onClick={onShowUploader}
              className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Upload
            </button>
          </div>

          {/* Combine Suggestions */}
          {combineSuggestions
            .filter((s) => !dismissedSuggestions.has(suggestionKey(s)))
            .map((suggestion) => {
              const key = suggestionKey(suggestion);
              return (
                <div key={key} className="flex items-center justify-between gap-4 mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xl flex-shrink-0">📎</span>
                    <p className="text-sm text-blue-800 dark:text-blue-200 min-w-0">
                      These {suggestion.memberDocumentIds.length + 1} documents appear to be parts of one policy (<span className="font-medium">{suggestion.label}</span>).
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => dismissCombineSuggestion(suggestion)}
                      className="px-3 py-1.5 text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 transition-colors"
                    >
                      Dismiss
                    </button>
                    <button
                      onClick={() => applyCombineSuggestion(suggestion)}
                      disabled={applyingSuggestionKey === key}
                      className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                    >
                      {applyingSuggestionKey === key ? 'Combining…' : 'Combine'}
                    </button>
                  </div>
                </div>
              );
            })}

          {/* Documents Tiles */}
          <div className={isList ? 'flex flex-col gap-2' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
            {(currentNavigationView === 'project-details' ? getPaginatedProjectDocuments() : getPaginatedDocuments(selectedCustomer!.id)).map((doc) => (
              <div
                key={doc.id}
                id={`doc-card-${doc.id}`}
                className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow group relative ${isList ? 'px-4 py-2.5' : 'p-4'} ${flashedDocumentId === doc.id ? 'ring-2 ring-primary' : ''}`}
              >
                {/* Checkbox */}
                <div className="absolute top-3 left-3 z-10">
                  <input
                    type="checkbox"
                    checked={customerSelectedDocumentIds.includes(doc.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      onDocumentCheckboxChange(doc.id, e.target.checked);
                    }}
                    className="w-4 h-4 text-primary bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary focus:ring-2"
                  />
                </div>

                {/* Clickable document area */}
                <div
                  className="cursor-pointer"
                  onClick={() => onDocumentClick(doc)}
                >
                  <div className="flex items-start justify-between mb-3 pl-6">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-green-600 dark:text-green-400">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-secondary dark:text-dark-text text-sm leading-tight break-words flex items-center gap-2">
                          <span>{doc.name}</span>
                        </h4>
                    </div>
                    </div>
                    {canEdit && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteDocument(doc.id, doc.name, e);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 transition-opacity flex-shrink-0 ml-2"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className={isList ? 'flex items-center gap-4 pl-6 flex-wrap' : 'space-y-2 pl-6'}>
                    <div className="flex items-center gap-2 text-sm text-accent dark:text-dark-muted">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5A2,2 0 0,1 5,3H6V1H8V3H16V1H18V3H19M19,19V8H5V19H19M7,16H9V14H7V16M13,16H11V14H13V16M17,16H15V14H17V16Z" />
                      </svg>
                      {formatRelativeDate(doc.uploadedAt)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-accent dark:text-dark-muted">
                      <DocumentIntelligenceChip documentId={doc.id} />
                    </div>
                    {doc.policyType === 'combined' && (
                      <div className="flex items-center gap-2 text-sm text-accent dark:text-dark-muted">
                        <CombinedDocumentChip documentId={doc.id} onUncombined={() => onDocumentsChanged?.()} />
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-accent dark:text-dark-muted">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                      {(doc.size / (1024 * 1024)).toFixed(2)} MB
                    </div>
                  </div>
                  </div>
                </div>
              ))}
            </div>

          {/* Documents Pagination */}
          {(currentNavigationView === 'project-details' ? documents.length : getCustomerDocuments(selectedCustomer!.id).length) > DOCUMENTS_PER_PAGE && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-accent dark:text-dark-muted">
                Showing {documentsPage * DOCUMENTS_PER_PAGE + 1} to {Math.min((documentsPage + 1) * DOCUMENTS_PER_PAGE, (currentNavigationView === 'project-details' ? documents.length : getCustomerDocuments(selectedCustomer!.id).length))} of {(currentNavigationView === 'project-details' ? documents.length : getCustomerDocuments(selectedCustomer!.id).length)} documents
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onPrevDocumentsPage}
                  disabled={documentsPage === 0}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-accent dark:text-dark-muted">
                  Page {documentsPage + 1} of {(currentNavigationView === 'project-details' ? getTotalProjectDocumentsPages() : getTotalDocumentsPages(selectedCustomer!.id))}
                </span>
                <button
                  onClick={onNextDocumentsPage}
                  disabled={documentsPage >= ((currentNavigationView === 'project-details' ? getTotalProjectDocumentsPages() : getTotalDocumentsPages(selectedCustomer!.id)) - 1)}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {(currentNavigationView === 'project-details' ? documents.length : getCustomerDocuments(selectedCustomer!.id).length) === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-2">
                No documents yet. Drop files anywhere on this tab or click Upload.
              </h3>
              <p className="text-accent dark:text-dark-muted mb-4">
                Risk Ninja automatically reads each document — carrier, policy number, dates — and recommends the right workflows.
              </p>
              <button
                onClick={onShowUploader}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Upload Documents
              </button>
            </div>
          )}

          {documentsRenameModalProps && <BatchRenameModal {...documentsRenameModalProps} />}
        </div>
      ) : customerDetailView === 'reports' ? (
        <div>
          {/* Reports Tiles */}
          <div className={isList ? 'flex flex-col gap-2' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
            {(currentNavigationView === 'project-details' ? getPaginatedProjectReports(selectedProject!.id) : getPaginatedReports(selectedCustomer!.id)).map((report) => (
              <div
                key={report.id}
                className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow group relative ${isList ? 'px-4 py-2.5' : 'p-4'}`}
              >
                {/* Checkbox */}
                <div className="absolute top-3 left-3 z-10">
                  <input
                    type="checkbox"
                    checked={customerSelectedReportIds.includes(report.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      onReportCheckboxChange(report.id, e.target.checked);
                    }}
                    className="w-4 h-4 text-primary bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary focus:ring-2"
                  />
        </div>

                {/* Clickable report area */}
                <div
                  className="cursor-pointer"
                  onClick={() => onReportClick(report)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-2 flex-1 min-w-0 ml-6">
                      {(() => {
                        // Check if this is a PDF report
                        let isPdfReport = false;
                        try {
                          if ('additionalFacts' in report && report.additionalFacts) {
                            const metadata = JSON.parse(report.additionalFacts);
                            isPdfReport = metadata.openAs === 'pdf_viewer' || metadata.isPdfReport === true;
                          }
                        } catch (e) {
                          // Fallback check
                          isPdfReport = ('primaryPolicyType' in report && report.primaryPolicyType === 'certificate_of_effort') ||
                                       report.title?.includes('Certificate of Effort');
                        }

                        return (
                          <div className={`w-8 h-8 ${
                            isPdfReport
                              ? 'bg-red-100 dark:bg-red-900'
                              : 'bg-purple-100 dark:bg-purple-900'
                          } rounded-full flex items-center justify-center flex-shrink-0`}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className={
                              isPdfReport
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-purple-600 dark:text-purple-400'
                            }>
                              {isPdfReport ? (
                                // PDF icon
                                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.5,17.5H8.5V15.5H10.5C10.78,15.5 11,15.72 11,16V17C11,17.28 10.78,17.5 10.5,17.5M10.5,16.5H9.5V16H10.5V16.5M13.5,17.5H11.5V12.5H13.5A2,2 0 0,1 15.5,14.5V15.5A2,2 0 0,1 13.5,17.5M13.5,16.5A1,1 0 0,0 14.5,15.5V14.5A1,1 0 0,0 13.5,13.5H12.5V16.5H13.5Z" />
                              ) : (
                                // Regular report icon
                                <path d="M9,12H15V10.5H9V12M9,16H15V14.5H9V16M9,8H15V6.5H9V8M5,4V20A2,2 0 0,0 7,22H17A2,2 0 0,0 19,20V4A2,2 0 0,0 17,2H7A2,2 0 0,0 5,4M7,4H17V20H7V4Z" />
                              )}
                            </svg>
                          </div>
                        );
                      })()}
                      <div className="flex-1 min-w-0">
                        {editingReportId === report.id ? (
                          <input
                            type="text"
                            value={editingReportTitle}
                            onChange={(e) => setEditingReportTitle(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => onSaveReportTitle(report.id)}
                            onKeyPress={(e) => e.key === 'Enter' && onSaveReportTitle(report.id)}
                            className="w-full text-sm font-medium bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
                            autoFocus
                          />
                        ) : (
                          <h4 className="font-medium text-secondary dark:text-dark-text text-sm leading-tight break-words">
                            {report.title}
                          </h4>
                        )}
                    </div>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditReportTitle(report.id, report.title);
                            }}
                            className="text-blue-500 hover:text-blue-700 p-1"
                            title="Edit title"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              onDeleteReport(report.id, report.title, e);
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Delete report"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  <div className="space-y-2 ml-6">
                    <div className="flex items-center gap-2 text-sm text-accent dark:text-dark-muted">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9,10H7V12H9V10M13,10H11V12H13V10M17,10H15V12H17V10M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5A2,2 0 0,1 5,3H6V1H8V3H16V1H18V3H19M19,19V8H5V19H19M7,16H9V14H7V16M13,16H11V14H13V16M17,16H15V14H17V16Z" />
                      </svg>
                      {report.createdAt && !isNaN(new Date(report.createdAt).getTime())
                        ? new Date(report.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                        : 'Unknown date'}
                    </div>
                  </div>
                  </div>
                </div>
              ))}
            </div>

          {/* Reports Pagination */}
          {(currentNavigationView === 'project-details' ? getProjectReports(selectedProject!.id).length : getCustomerReports(selectedCustomer!.id).length) > REPORTS_PER_PAGE && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-accent dark:text-dark-muted">
                Showing {reportsPage * REPORTS_PER_PAGE + 1} to {Math.min((reportsPage + 1) * REPORTS_PER_PAGE, (currentNavigationView === 'project-details' ? getProjectReports(selectedProject!.id).length : getCustomerReports(selectedCustomer!.id).length))} of {(currentNavigationView === 'project-details' ? getProjectReports(selectedProject!.id).length : getCustomerReports(selectedCustomer!.id).length)} reports
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onPrevReportsPage}
                  disabled={reportsPage === 0}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-accent dark:text-dark-muted">
                  Page {reportsPage + 1} of {(currentNavigationView === 'project-details' ? getTotalProjectReportsPages(selectedProject!.id) : getTotalReportsPages(selectedCustomer!.id))}
                </span>
                <button
                  onClick={onNextReportsPage}
                  disabled={reportsPage >= ((currentNavigationView === 'project-details' ? getTotalProjectReportsPages(selectedProject!.id) : getTotalReportsPages(selectedCustomer!.id)) - 1)}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {(currentNavigationView === 'project-details' ? getProjectReports(selectedProject!.id).length : getCustomerReports(selectedCustomer!.id).length) === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400">
                  <path d="M9,12H15V10.5H9V12M9,16H15V14.5H9V16M9,8H15V6.5H9V8M5,4V20A2,2 0 0,0 7,22H17A2,2 0 0,0 19,20V4A2,2 0 0,0 17,2H7A2,2 0 0,0 5,4M7,4H17V20H7V4Z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-2">
                No underwriting reports yet
              </h3>
              <p className="text-accent dark:text-dark-muted mb-4">
                Generate underwriting reports for this customer to get started with risk analysis.
              </p>
            </div>
          )}
        </div>
      ) : null}

      {/* Proposals Section */}
      {customerDetailView === 'proposals' && (
        <ProposalsTab
          customerId={selectedCustomer!.id}
          projectId={currentNavigationView === 'project-details' ? selectedProject?.id : undefined}
          reports={currentNavigationView === 'project-details'
            ? getProjectReports(selectedProject!.id)
            : getCustomerReports(selectedCustomer!.id)}
          documents={currentNavigationView === 'project-details'
            ? documents.filter((d) => (d as any).projectId === selectedProject?.id)
            : documents}
          canEdit={canEdit}
          viewMode={contentViewMode}
        />
      )}

      {/* Insurance Summary Section */}
      {customerDetailView === 'insurance_summary' && selectedProject && (
        <InsuranceSummaryView
          customerId={selectedCustomer!.id}
          customerName={selectedCustomer!.lineType === 'personal' ? selectedCustomer!.name : (selectedCustomer!.company || selectedCustomer!.name)}
          projectId={selectedProject.id}
          projectTitle={selectedProject.title}
          documents={documents}
          canEdit={canEdit}
          autoOpenSummaryId={autoOpenInsuranceSummaryId || null}
          onAutoOpenConsumed={onAutoOpenConsumed}
        />
      )}
      {customerDetailView === 'insurance_summary' && !selectedProject && (
        <div className="text-center py-12">
          <p className="text-accent dark:text-dark-muted">
            Insurance Summaries live inside a project folder. Open a project to view or create summaries.
          </p>
        </div>
      )}

      {/* Recommended workflow "Run" — pre-filled with the matched documents/context */}
      {runningSuggestion && (
        <WorkflowModal
          isOpen={true}
          onClose={() => {
            setRunningSuggestion(null);
            fetchWorkflowSuggestions();
          }}
          workflowType={runningSuggestion.workflowType as any}
          clientId={selectedCustomer!.id}
          clientName={selectedCustomer!.lineType === 'personal' ? selectedCustomer!.name : (selectedCustomer!.company || selectedCustomer!.name)}
          clientType={selectedCustomer!.type}
          projectId={selectedProject?.id}
          projectName={selectedProject?.title}
          initialSelectedDocumentIds={runningSuggestion.documentIds}
          initialContext={runningSuggestion.prefillContext}
        />
      )}
    </div>
  );
};

// Proposals list component for the Proposals tab
const ProposalsList: React.FC<{ customerId: string; projectId?: string; refreshKey?: number; viewMode?: 'card' | 'list' }> = ({ customerId, projectId, refreshKey, viewMode = 'card' }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

  const fetchProposals = useCallback(async () => {
    try {
      let url = `${apiUrl}/api/proposals?customerId=${customerId}`;
      if (projectId) url += `&projectId=${projectId}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setProposals(data.proposals || []);
      }
    } catch (err) {
      console.error('Failed to fetch proposals:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, customerId, projectId]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals, refreshKey]);

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return <ProposalsListInner loading={loading} proposals={proposals} navigate={navigate} viewMode={viewMode} />;
};

// Presentational portion of the proposals list, split out so the Proposals tab
// wrapper can render the "Create Proposal" header above it.
const ProposalsListInner: React.FC<{ loading: boolean; proposals: any[]; navigate: ReturnType<typeof useNavigate>; viewMode?: 'card' | 'list' }> = ({ loading, proposals, navigate, viewMode = 'card' }) => {

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M9,13V15H15V13H9M9,17V19H12V17H9M9,9V11H15V9H9Z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-secondary dark:text-dark-text mb-2">
          No proposals yet
        </h3>
        <p className="text-accent dark:text-dark-muted">
          Click "Create Proposal" to build one from your reports and documents using your templates.
        </p>
      </div>
    );
  }

  return (
    <div className={viewMode === 'list' ? 'flex flex-col gap-2' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
      {proposals.map((proposal: any) => (
        <div
          key={proposal.id}
          onClick={() => navigate(`/proposal-editor/${proposal.id}`)}
          className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md hover:border-primary transition-all cursor-pointer ${viewMode === 'list' ? 'px-4 py-2.5' : 'p-4'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">{proposal.title}</h4>
              <div className="flex items-center gap-3 mt-1">
                {proposal.template && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Template: {proposal.template.title}
                  </span>
                )}
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                  proposal.status === 'final'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                }`}>
                  {proposal.status === 'final' ? 'Final' : 'Draft'}
                </span>
                <span className="text-xs text-gray-400">
                  {formatRelativeDate(proposal.createdAt)}
                </span>
              </div>
            </div>
            <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" />
            </svg>
          </div>
        </div>
      ))}
    </div>
  );
};

// Proposals tab wrapper: "Create Proposal" header + the list. Owns the create
// modal and a refresh counter so the list re-fetches after a proposal is made.
const ProposalsTab: React.FC<{
  customerId: string;
  projectId?: string;
  reports: any[];
  documents: PolicyDocument[];
  canEdit: boolean;
  viewMode?: 'card' | 'list';
}> = ({ customerId, projectId, reports, documents, canEdit, viewMode = 'card' }) => {
  const { token } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div>
      {canEdit && (
        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 text-sm font-medium"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
            </svg>
            Create Proposal
          </button>
        </div>
      )}

      <ProposalsList customerId={customerId} projectId={projectId} refreshKey={refreshKey} viewMode={viewMode} />

      {showCreate && (
        <CreateProposalModal
          customerId={customerId}
          projectId={projectId}
          reports={reports}
          documents={documents}
          token={token}
          onClose={() => {
            setShowCreate(false);
            // Re-fetch so a freshly generated proposal shows up.
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
};

export default ProjectDetailView;
